import React, { memo, useState } from 'react';
import {
  LoadingContainer,
  Space,
  PanelCompatibleContainer,
} from '@redlink/components/atoms';
import { View, TouchableOpacity, Image, ScrollView } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import { SetupPageTitle, Button } from '@redlink/components/molecules';
import { ImageCircularUploaderSimple } from '@redlink/components/muscles';
import { useQuery, getAvatarsQuery } from '@redlink/services';
import { isMobile } from 'react-device-detect';
import { useMutation, editPageAvatarQuery } from '@redlink/services';
import { useNavigation, StackActions } from '@react-navigation/native';

export const emptyAvatar = { id: null, thumbnailUrl: '' };
const AVATAR_SIZE = 80;
const AVATAR_SELECT_BORDER_SIZE = 3;

export const PageAvatarSetupPage = memo(({ navigation }) => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { routes } = useValues();
  const [avatar, setAvatar] = useState(emptyAvatar);
  const { dispatch: navigationDispatch } = useNavigation();

  const [edit, { loading }] = useMutation(editPageAvatarQuery, {
    done: () =>
      navigationDispatch(StackActions.replace(routes.pageTagsSetup.key)),
  });

  const {
    loading: getAvatarsIsBusy,
    data: { Avatars = [] },
  } = useQuery(getAvatarsQuery);

  return (
    <LoadingContainer
      isBusy={getAvatarsIsBusy || loading}
      style={styles.container}
    >
      <PanelCompatibleContainer>
        <SetupPageTitle
          title={dictionary.setupPageTitle}
          subtitle={dictionary.setupPageAvatarTitle}
          description={dictionary.setupPageAvatarDescription}
          hints={dictionary.setupPageAvatarHints}
          step={3}
        />
        <Space size={8} />
        <AvatarSelect
          avatars={Avatars}
          selectedAvatar={avatar}
          onChange={(item) => setAvatar(item)}
        />
        <Space size={8} />
        <Button
          style={styles.formActionContainer}
          title={dictionary.continue}
          variant="primary"
          onPress={() => {
            edit({
              assetId: avatar.id,
            });
          }}
        />
        <Space />
      </PanelCompatibleContainer>
    </LoadingContainer>
  );
});

const AvatarSelect = memo(({ avatars, selectedAvatar, onChange }) => {
  const [uploadeAvatar, setUploadedAvatar] = useState(emptyAvatar);
  const { styles } = useStyle();
  return (
    <ScrollView>
      <View style={styles.avatarsContainer}>
        {avatars.map((item, index) => {
          const normalizedItem = index === 0 ? uploadeAvatar : item;
          const selected =
            normalizedItem.id !== null &&
            normalizedItem.id === selectedAvatar.id;
          return (
            <TouchableOpacity
              activeOpacity={0.7}
              disabled={normalizedItem.id === null}
              style={[
                styles.avatarContainer,
                selected && styles.avatarContainerSelected,
              ]}
              onPress={() => onChange(normalizedItem)}
            >
              {index === 0 && (
                <ImageCircularUploaderSimple
                  size={AVATAR_SIZE - AVATAR_SELECT_BORDER_SIZE * 2}
                  thumbnail={normalizedItem.thumbnailUrl}
                  value={normalizedItem}
                  onUpload={(result) => {
                    setUploadedAvatar(result || emptyAvatar);
                    onChange(result || emptyAvatar);
                  }}
                />
              )}
              {index !== 0 && (
                <Image
                  source={{ uri: normalizedItem.thumbnailUrl }}
                  style={styles.avatarImage}
                />
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    paddingHorizontal: isMobile ? size.mobilePagePadding : size.pagePadding,
    paddingVertical: isMobile
      ? size.mobilePagePadding
      : size.verticalPagePadding,
  },
  formActionContainer: {
    minHeight: 50,
  },
  avatarsContainer: {
    flex: 1,
    flexDirection: 'row-reverse',
    gap: 5,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  avatarContainer: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 100,
    borderWidth: AVATAR_SELECT_BORDER_SIZE,
    borderColor: colors.white,
    backgroundColor: colors.gray7,
  },
  avatarContainerSelected: {
    borderColor: colors.blue1,
  },
  avatarImage: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: 100,
    overflow: 'hidden',
  },
}));
